import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('lawnServiceProvider');
export interface LawnServiceProviderInfoRec {
  id: string;
  userId: string;
  companyName?: string;
  accountNumber?: string;
  serviceAddress?: string;
  billingAddress?: string;
  contactPhone?: string;
  emergencyPhone?: string;
  website?: string;
  serviceType?: string;
  serviceFrequency?: string;
  estimatedBalance?: number;
  contractStartDate?: string;
  contractEndDate?: string;
  notes?: string;
  username?: string;
  password?: string;
  isLocked?: boolean;
}

class LawnServiceProviderInfoService {
  async getAllLawnServiceProviderInfo(): Promise<LawnServiceProviderInfoRec[]> {
    const response = await http.get('/api/lawn-service-provider-info');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch lawn service provider information');
    }

    return response.data.data || [];
  }

  async getLawnServiceProviderInfoById(id: string): Promise<LawnServiceProviderInfoRec | null> {
    try {
      const response = await http.get(`/api/lawn-service-provider-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch lawn service provider information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching lawn service provider information by ID:', error);
      return null;
    }
  }

  async createLawnServiceProviderInfo(lawnServiceProviderData: Partial<LawnServiceProviderInfoRec>): Promise<LawnServiceProviderInfoRec> {
    const response = await http.post('/api/lawn-service-provider-info', lawnServiceProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save lawn service provider information');
    }

    return response.data.data;
  }

  async updateLawnServiceProviderInfo(id: string, lawnServiceProviderData: Partial<LawnServiceProviderInfoRec>): Promise<LawnServiceProviderInfoRec> {
    const response = await http.patch(`/api/lawn-service-provider-info/${id}`, lawnServiceProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update lawn service provider information');
    }

    return response.data.data;
  }

  async deleteLawnServiceProviderInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/lawn-service-provider-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete lawn service provider information');
    }
  }

  async lockLawnServiceProviderInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for lawn service provider ID:', id);
    const response = await http.post(`/api/lawn-service-provider-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock lawn service provider information');
    }
  }

  async unlockLawnServiceProviderInfo(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for lawn service provider ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/lawn-service-provider-info/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock lawn service provider information');
    }
  }

  async getLawnServiceProviderAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/lawn-service-provider-info/${id}/access-grants`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch lawn service provider access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching lawn service provider access grants:', error);
      return [];
    }
  }
}

export const lawnServiceProviderInfoService = new LawnServiceProviderInfoService();